import type { ComponentType } from 'react';

/** Marker colour: the duty status of the vehicle (sleeper has its own colour), unidentified driving, or no status. */
export type MarkerTone = 'driving' | 'on' | 'sleeper' | 'off' | 'unidentified' | 'unknown';

/** CSS colour per tone, from the theme tokens (switch between night and day by themselves). */
export const TONE_COLOR: Record<MarkerTone, string> = {
  driving: 'var(--duty-d)',
  on: 'var(--duty-on)',
  sleeper: 'var(--duty-sb)',
  off: 'var(--duty-off)',
  unidentified: 'var(--destructive)',
  unknown: 'var(--muted-foreground)',
};

export interface MapMarker {
  id: string;
  lat: number;
  lon: number;
  /** Short text drawn on the marker (unit number). */
  label: string;
  /** Accessible description, e.g. "Unit 101, Jim Trucker, Driving". */
  description: string;
  tone: MarkerTone;
  headingDeg?: number | null;
}

/**
 * Colour of an overlay: a geofence, the selected one, a restricted area, the shape being drawn, or a driven route
 * line by speed band (route-slow / route-mid / route-fast, drawn thicker).
 */
export type ShapeTone = 'geofence' | 'selected' | 'restricted' | 'draft' | 'inactive' | 'route-slow' | 'route-mid' | 'route-fast';

/** CSS variable per shape tone (MapLibre resolves them with getComputedStyle). */
export const SHAPE_VAR: Record<ShapeTone, string> = {
  geofence: '--info',
  selected: '--primary',
  restricted: '--destructive',
  draft: '--primary',
  inactive: '--muted-foreground',
  'route-slow': '--warning',
  'route-mid': '--info',
  'route-fast': '--duty-d',
};

/** Stroke width of an overlay outline / line. */
export function shapeWidth(tone: ShapeTone): number {
  if (tone.startsWith('route-')) return 4;
  return tone === 'selected' || tone === 'draft' ? 2.5 : 1.5;
}

/** Area overlay (geofence). Polygon points are [lon, lat] (GeoJSON order); `open` = still being drawn (no fill). */
export type MapShape = {
  id: string;
  /** Accessible name, e.g. "Geofence Joliet yard (yard)". */
  label: string;
  tone: ShapeTone;
} & ({ kind: 'circle'; center: { lat: number; lon: number }; radiusM: number } | { kind: 'polygon'; points: [number, number][]; open?: boolean });

export interface MapViewProps {
  markers: MapMarker[];
  /** Area overlays drawn under the markers. */
  shapes?: MapShape[];
  onSelectShape?: (id: string) => void;
  /** Clicks on the map background (drawing). The map shows a crosshair cursor while set. */
  onMapClick?: (p: { lat: number; lon: number }) => void;
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  /** Accessible name of the map region. */
  label: string;
  className?: string;
  /** Refit the view to the markers whenever this changes (default: fit once, on the first markers). */
  fitKey?: string;
}

export type MapImplementation = ComponentType<MapViewProps>;
